"use client";

import { AnimatePresence, motion } from "framer-motion";
import { ICON_SIZES, Icon } from "@/components/library/Icon";

import clsx from "clsx";
import { useState } from "react";

export type FaqItem = {
  question: string;
  answer: string;
};

export type MarketingPricingFaqProps = {
  title: string;
  items: FaqItem[];
  className?: string;
};

export function MarketingPricingFaq({
  title,
  items,
  className,
}: Readonly<MarketingPricingFaqProps>) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className={clsx("flex justify-center px-4 pb-20 pt-8", className)}>
      <div className="w-full max-w-6xl">
        <h2 className="font-monument text-[2rem] font-normal leading-[1.12] tracking-tight text-black md:text-[2.5rem]">
          {title}
        </h2>

        <div className="mt-8 border-b border-black/10">
          {items.map((item, index) => {
            const isOpen = openIndex === index;

            return (
              <div key={item.question} className="border-t border-black/10">
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-6 py-6 text-left"
                >
                  <span className="font-monument text-lg text-black md:text-xl">
                    {item.question}
                  </span>
                  <span
                    className={clsx(
                      "shrink-0 text-black/70 transition-transform duration-300 ease-out",
                      isOpen && "rotate-45",
                    )}
                  >
                    <Icon icon="plus" size={ICON_SIZES.LG} />
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen ? (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-3xl pb-6 font-monument text-base leading-relaxed text-black/70">
                        {item.answer}
                      </p>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
